import { CSSObject } from "@emotion/react";
import { LoadingSpinner } from "@newsela/angelou";
import { isUndefined } from "@s-libs/micro-dash";

import Icon from "@/components/Icon";
import getCommaSeparatedNumericString from "@/utils/getCommaSeparatedNumericString";
import { DataInfoCardVariant, StatValueProps } from "../DataInfoCard";
import {
  blueIconCss,
  circleShapeCss,
  circleShapeWithProgressVariantCss,
  defaultVariantCss,
  defaultVariantWithStatValueCSS,
  iconContainerCss,
  progressCircleCss,
  progressCircleVariantCss,
  statusTextCss,
  statValueCircleVariantCss,
  statValueNumberCss,
  DataValueOrIconContainerCss,
  smallPercentageSymbolCss,
} from "./DataValueOrIcon.style";

const DEFAULT_PROGRESS_PERCENT_BASE = 100;

const DataValueOrIcon = ({
  statValue,
  statValueAddOn,
  statValuePrefix,
  additionalStyles,
  variant,
  progressPercentBase = DEFAULT_PROGRESS_PERCENT_BASE,
  statusText,
  iconSvgComponent,
  showPercentageSymbol,
  loading,
}: StatValueProps) => {
  const progressPercentValue = () => {
    if (statValue === undefined || progressPercentBase < 1) return 0;
    const rawPercentage = (100 * statValue) / progressPercentBase;
    return Math.round(Math.min(100, Math.max(0, rawPercentage)));
  };

  const getVariantCss = (): CSSObject | CSSObject[] => {
    switch (variant) {
      case DataInfoCardVariant.circle:
      case DataInfoCardVariant.icon:
        return statValueCircleVariantCss;
      case DataInfoCardVariant.progressCircle:
        return progressCircleVariantCss;
      default:
        return {};
    }
  };
  const shouldDisplayLoadingStateWithinIcon = (): boolean => {
    if (!variant) return false;
    return [
      DataInfoCardVariant.icon,
      DataInfoCardVariant.circle,
      DataInfoCardVariant.progressCircle,
    ].includes(variant);
  };

  if (isUndefined(statValue) && !iconSvgComponent && !statusText) return <></>;

  return (
    <div css={DataValueOrIconContainerCss}>
      <div
        css={[
          defaultVariantCss,
          getVariantCss(),
          variant === DataInfoCardVariant.default &&
            statValue &&
            defaultVariantWithStatValueCSS,
          additionalStyles?.statValueCss,
        ]}
      >
        {variant === DataInfoCardVariant.icon && !!iconSvgComponent && (
          <div css={[iconContainerCss, blueIconCss]} data-testid="status-icon">
            <Icon SvgComponent={iconSvgComponent} />
          </div>
        )}

        {shouldDisplayLoadingStateWithinIcon() && !!loading ? (
          <div css={[iconContainerCss, blueIconCss]} data-testid="status-icon">
            <LoadingSpinner
              __cssFor={{ root: { margin: "0px", width: "100%" } }}
            />
          </div>
        ) : null}

        {variant !== DataInfoCardVariant.default && (
          <div
            css={[
              circleShapeCss,
              variant === DataInfoCardVariant.progressCircle &&
                circleShapeWithProgressVariantCss,
            ]}
          />
        )}
        {variant === DataInfoCardVariant.progressCircle &&
          statValue !== 0 &&
          !loading && <div css={progressCircleCss(progressPercentValue())} />}

        {statValue !== undefined &&
          variant !== DataInfoCardVariant.icon &&
          !loading && (
            <strong
              css={[statValueNumberCss, additionalStyles?.statValueNumberCss]}
            >
              {statValuePrefix && <span>{statValuePrefix}</span>}
              {getCommaSeparatedNumericString(statValue)}
              {statValueAddOn && (
                <span css={additionalStyles?.statValueAddOnCss}>
                  {" "}
                  {statValueAddOn}
                </span>
              )}
              {showPercentageSymbol && (
                <span
                  css={
                    variant === DataInfoCardVariant.progressCircle &&
                    smallPercentageSymbolCss
                  }
                >
                  %
                </span>
              )}
            </strong>
          )}
      </div>
      {statusText && <div css={statusTextCss}>{statusText}</div>}
    </div>
  );
};

export default DataValueOrIcon;
